import { useState } from "react"
import { useStripe, useElements } from "@stripe/react-stripe-js"
import AldooStoreAPI from "aldoo-ra/Store/api"
import { api } from "aldoo-ra/API"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import { CreateUser, RequestInternalID, Login, VerifyCredentials } from "aldoo-ra/PlatformAuth/api"
import ensureDevice from "aldoo-ra/PlatformAuth/actions/ensure-device"
import awaitAuth from "aldoo-ra/PlatformAuth/actions/await-auth"
import Value from "aldoo-ra/Value"
import PubSub from "pubsub-js"

const { useAuth } = PlatformAuth

const usePaymentSubmission = (store) => {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(false)
  const [auth, setAuth] = useAuth()
  const { onPaymentSuccess, paymentInfo, onPaymentError, customAuth } = store

  // Use custom auth if available (for existing users not logged in)
  const storeAPIFunc = customAuth ? api("store", customAuth) : null
  const storeAPI = customAuth ? {
    completeSubscriptionAfterSetup: async (payload) => {
      return await storeAPIFunc({ complete_subscription_after_setup: { platform: "Web", ...payload } })
    },
    updateCustomerInvoiceDetails: async (payload) => {
      return await storeAPIFunc({ update_customer_invoice_details: { platform: "Web", ...payload } })
    },
    updateCustomerEmail: async (email) => {
      return await storeAPIFunc({ "update-customer-email": { platform: "Web", email } })
    },
  } : AldooStoreAPI

  const completeSubscriptionAfterSetup = async (setupIntentId) => {
    try {
      const response = await storeAPI.completeSubscriptionAfterSetup({
        pid: paymentInfo.pid,
        setupIntentId,
        promo_code: paymentInfo.promoCode,
      })
      if (response.error) throw new Error(response.error)

      // If subscription requires immediate payment, confirm the payment intent
      if (response.requiresPayment && response.clientSecret) {
        const { error: paymentError } = await stripe.confirmPayment({
          clientSecret: response.clientSecret,
          redirect: 'if_required'
        })

        if (paymentError) {
          throw new Error(paymentError.message)
        }
      }

      return response
    } catch (error) {
      throw error
    }
  }

  const handleSubmitWithSavedMethod = async (invoiceData) => {
    if (!stripe) return
    // the payment is being made (the store reports GA4 add_payment_info with the cart)
    PubSub.publish("STORE_PAYMENT_SUBMIT", { payment_type: "saved card" })

    setLoading(true)
    try {
      // If invoice is requested, update customer details first
      if (invoiceData?.needsInvoice) {
        await storeAPI.updateCustomerInvoiceDetails({
          companyName: invoiceData.companyName,
          taxId: invoiceData.taxId,
          address: invoiceData.address,
        })
      }

      // First determine if we're dealing with a SetupIntent or PaymentIntent
      const isSetupIntent = paymentInfo.clientSecret?.startsWith("seti_")
      const isPaymentIntent = paymentInfo.clientSecret?.startsWith("pi_")
      let result

      const paymentMethodType = paymentInfo.savedPaymentMethod.type

      if (isSetupIntent) {
        // When using a saved payment method, skip the setup intent confirmation
        // and directly create the subscription with the saved payment method
        const subscriptionResult = await completeSubscriptionAfterSetup(null) // Pass null for setupIntentId

        // Check for errors from backend
        if (subscriptionResult?.error) {
          console.error('❌ Subscription creation failed:', subscriptionResult)
          throw {
            message: subscriptionResult.message || 'Subscription creation failed',
            code: subscriptionResult.error,
            decline_code: subscriptionResult.decline_code,
            payment_intent: { id: subscriptionResult.payment_intent_id }
          }
        }

        // Store the returned payment intent ID and subscription ID
        if (subscriptionResult?.paymentIntentId) {
          store.setPaymentIntentId(subscriptionResult.paymentIntentId)
        }
        if (subscriptionResult?.subscriptionId) {
          store.setSubscriptionId(subscriptionResult.subscriptionId)
        }

        // Create a synthetic result object
        result = {
          setupIntent: { status: 'succeeded', id: 'saved_method' },
          subscriptionResult
        }
      } else if (isPaymentIntent) {
        // For payment intents, use the appropriate confirmation method based on payment method type
        if (paymentMethodType === 'card') {
          result = await stripe.confirmCardPayment(
            paymentInfo.clientSecret,
            {
              payment_method: paymentInfo.savedPaymentMethod.id,
            }
          )
        } else {
          // For non-card payment methods (like Link), use generic confirmPayment
          result = await stripe.confirmPayment({
            clientSecret: paymentInfo.clientSecret,
            confirmParams: {
              payment_method: paymentInfo.savedPaymentMethod.id,
              return_url: window.location.href, // Required for some payment methods
            },
            redirect: 'if_required'
          })
        }
      } else if (paymentInfo.type === "free_subscription") {
        // Free subscriptions don't need confirmation
        result = { error: null }
      } else {
        throw new Error("Unknown intent type")
      }

      if (result.error) {
        throw result.error
      }

      // Capture payment intent ID from result if available
      let capturedPaymentIntentId = null
      let capturedSubscriptionId = null

      if (result.paymentIntent) {
        // Direct payment intent (for one-time payments or immediate subscription payments)
        capturedPaymentIntentId = result.paymentIntent.id
      } else if (result.subscriptionResult) {
        // Setup intent that created a subscription - get IDs from subscription result
        capturedPaymentIntentId = result.subscriptionResult.paymentIntentId
        capturedSubscriptionId = result.subscriptionResult.subscriptionId
      }

      onPaymentSuccess({
        paymentIntentId: capturedPaymentIntentId,
        subscriptionId: capturedSubscriptionId
      })
    } catch (error) {
      onPaymentError(error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // A flow may add confirmParams (e.g. billing_details collected by its own
  // address step): getConfirmParams: ({ store, email }) => ({ payment_method_data })
  const flowConfirmParams = (email) => {
    const fn = store.purchaseFlow?.flowDefinition?.getConfirmParams
    return (typeof fn === 'function' ? fn({ store, email }) : null) || {}
  }

  const handleAnonymousPurchase = async (email) => {
    // Email verification and user setup is now done in handleEmailVerification (before checkout)
    // This function now just updates the Stripe customer email if needed

    // Stores whose customer email is set server-side skip the sync call
    // (config.payment.syncCustomerEmail = false)
    if (store.config?.payment?.syncCustomerEmail === false) return null

    // If email is provided and customer doesn't have it yet, update it
    if (email) {
      try {
        await storeAPI.updateCustomerEmail(email)
      } catch (error) {
        // Don't throw - continue with payment even if email update fails
      }
    }

    // Auth is already set up at this point - no need to return verification info
    return null
  }

  const handleSubmit = async (invoiceData, email) => {
    setLoading(true)
    if (!stripe) return
    if (!elements) return
    PubSub.publish("STORE_PAYMENT_SUBMIT", { payment_type: "card" })
    const return_url = `${window.location.origin}/payment-result`

    try {
      // Update customer email if provided (for invoice generation)
      // Email verification and user setup is now done before checkout
      if (email) {
        await handleAnonymousPurchase(email)
      }

      // Don't update customer details here for non-invoice purchases
      // The webhook will extract billing details from the payment method automatically
      // This ensures the correct country and address are used from what the user entered in the payment form

      // If invoice is requested, extract address data and update customer
      if (invoiceData?.needsInvoice) {
        // Get email from form input or auth (must always have email for invoice generation)
        const invoiceEmail = email || auth?.email

        // Check if address is already in invoiceData (from saved customer data)
        if (invoiceData.address && invoiceData.addressComplete) {
          await storeAPI.updateCustomerInvoiceDetails({
            companyName: invoiceData.companyName,
            taxId: invoiceData.taxId,
            address: invoiceData.address,
            email: invoiceEmail,
          })
        } else {
          // Try to get address from address element
          const addressElement = elements.getElement('address')
          if (addressElement) {
            const { complete, value: addressValue } = await addressElement.getValue()

            if (complete && addressValue) {
              // Send invoice data to backend to update Stripe customer
              await storeAPI.updateCustomerInvoiceDetails({
                companyName: invoiceData.companyName,
                taxId: invoiceData.taxId,
                address: {
                  line1: addressValue.address.line1,
                  line2: addressValue.address.line2,
                  city: addressValue.address.city,
                  state: addressValue.address.state,
                  postal_code: addressValue.address.postal_code,
                  country: addressValue.address.country,
                },
                email: addressValue.email || invoiceEmail, // Prioritize: Stripe form email, then our collected email
                name: addressValue.name || undefined,
              })
            }
          }
        }
      }

      // Determine intent type from clientSecret prefix
      const isSetupIntent = paymentInfo.clientSecret?.startsWith("seti_")
      const isPaymentIntent = paymentInfo.clientSecret?.startsWith("pi_")

      // Regular payment flow with elements
      if (isSetupIntent || paymentInfo.type === "trial_setup") {
        // Handle trial setup with SetupIntent - confirm once
        const { setupIntent, error: setupError } = await stripe.confirmSetup({
          elements,
          redirect: "if_required",
          confirmParams: {
            return_url,
          },
        })

        if (setupError) return onPaymentError(setupError)

        // If setup succeeded, complete the subscription
        if (setupIntent && setupIntent.status === "succeeded") {
          const subscriptionResult = await completeSubscriptionAfterSetup(setupIntent.id)

          let capturedPaymentIntentId = null
          let capturedSubscriptionId = null

          // Store subscription ID for processing verification
          if (subscriptionResult?.subscriptionId) {
            capturedSubscriptionId = subscriptionResult.subscriptionId
            store.setSubscriptionId(capturedSubscriptionId)
          }

          // If there's a payment intent ID returned directly (for subscription schedules)
          if (subscriptionResult?.paymentIntentId) {
            capturedPaymentIntentId = subscriptionResult.paymentIntentId
            store.setPaymentIntentId(capturedPaymentIntentId)
          }
          // Legacy: If there's a payment intent for immediate payment via clientSecret
          else if (subscriptionResult?.clientSecret) {
            capturedPaymentIntentId = subscriptionResult.clientSecret.split('_secret_')[0]
            store.setPaymentIntentId(capturedPaymentIntentId)
          }

          // Payment is successful - pass IDs directly
          onPaymentSuccess({
            paymentIntentId: capturedPaymentIntentId,
            subscriptionId: capturedSubscriptionId
          })
          return
        } else {
          throw new Error('Setup intent did not succeed')
        }
      } else if (isPaymentIntent) {
        // Handle regular payment with PaymentIntent
        const result = await stripe.confirmPayment({
          elements,
          redirect: "if_required",
          confirmParams: {
            return_url,
            ...flowConfirmParams(email),
          },
        })

        if (result.error) {
          return onPaymentError(result.error)
        }

        if (result.paymentIntent) {
          // IMPORTANT: Use the payment intent ID from the confirmation result
          const actualPaymentIntentId = result.paymentIntent.id

          // Store payment intent ID for processing verification
          store.setPaymentIntentId(actualPaymentIntentId)

          // Payment is successful - pass payment intent ID
          onPaymentSuccess({
            paymentIntentId: actualPaymentIntentId
          })
          return
        }
      } else {
        throw new Error("Unknown intent type: unable to determine from clientSecret")
      }

      // Payment is successful (for non-payment-intent flows)
      onPaymentSuccess()
    } catch (err) {
      console.error('❌ Payment submission error:', err)
      onPaymentError(err)
    } finally {
      setLoading(false)
    }
  }

  return {
    handleSubmit,
    handleSubmitWithSavedMethod,
    loading,
    setLoading,
    ready,
    setReady,
    stripe,
    elements,
    isReady: stripe && ready && !loading,
  }
}

export default usePaymentSubmission
